import { useEffect, useState, useRef } from 'react';
import { CssBaseline } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { ThemeProvider } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import * as signalR from '@microsoft/signalr';
import { HttpTransportType } from '@microsoft/signalr';
import ContainerDetailOverview from 'components/pages/ContainerDetail';
import ContainerRelease from 'components/pages/ContainerRelease';
import Containers from 'components/pages/Containers';
import ContainerSubscriptions from 'components/pages/ContainerSubscriptions';
import ReleaseDetail from 'components/pages/ReleaseDetail';
import SubscriptionDetail from 'components/pages/SubscriptionDetail';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api, mainAPI } from '../modules/global/adapters';
import { bindActionCreators } from 'redux';
import { BetaRouteSwitch } from '../modules/global/components/BetaToggle';
import '../modules/global/config/i18n';
import { ROUTE_PATHS } from '../modules/shipping-line/constants';
import { RegularRoutes, Routes as BetaRoutes } from '../modules/shipping-line/routes';
import { acquireAccessToken } from 'auth/hooks/authProvider';
import {
  RegularTransportOperatorRoutes,
  BetaTransportOperatorRoutes,
} from '../modules/transport-operator/routes';
import bindConnectionMessages from '../signalR/connectionMessages';
import { signalRIdReceived, signalRSetupFailed } from '../store/actions/signalRActions';
import {
  loadCreditIdentificationResponse,
  loadUserProfile,
} from '../store/actions/userProfileActions';
import { retrieveSignalRData } from '../store/api/userApi';
import {
  getCreditIdentificationLoading,
  getCreditIdentificationResponse,
  getSelectedUserType,
  getSignalRId,
  getTokenExpired,
  getTokenExpiring,
  getUserProfile,
} from '../store/selectors/selectors';
import TermsAndConditions from '../components/organisms/TermsAndConditions';
import { BaseRoleCodes } from '../theme/constants';
import { CRPTheme } from '../theme/theme';
import BreadcrumbBar from './molecules/BreadcrumbBar';
import Messages from './molecules/Messages';
import ExpiredToken from './organisms/ExpiredToken';
import Topbar from './organisms/Topbar';
import Blocked from './pages/Blocked';
import ErrorPage from './pages/ErrorPage';
import DriverList from './pages/DriverList';
import PairedRedirect from './pages/PairedRedirect';
import Releases from './pages/Releases';
import RegularTerminalRoutes from 'modules/terminal/routes/RegularRoutes';
import BetaTerminalRoutes from 'modules/terminal/routes/Routes';
import { RegularReleasePartyRoutes, BetaReleasePartyRoutes } from 'modules/release-party/routes';
import Alfapass from './pages/Alfapass';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from 'authConfig';
import { InteractionStatus } from '@azure/msal-browser';
import LoginCallbackForAlfapass from 'components/pages/LoginCallbackForAlfapass';
import { hasBaseRole, hasPermission } from 'auth/helper';
import { Permission } from 'auth/permissions';

const styles = (theme) => ({
  root: {
    display: 'flex',
    marginBottom: theme.spacing(5),
  },
  content: {
    padding: theme.spacing(3),
    paddingTop: theme.spacing(12),
    width: '100%',
  },
  toolbar: theme.mixins.toolbar,
});

function App(props) {
  const { inProgress, instance: msalInstance, accounts } = useMsal();
  const [errored, setErrored] = useState(false);
  const [, setError] = useState(null);
  const signalRInitializingRef = useRef(false);
  const location = useLocation();

  const {
    classes,
    userProfile,
    selectedUserType,
    creditIdentificationResponse,
    loadingCreditIdentification,
    tokenExpired,
    tokenExpiring,
    loadCreditIdentificationResponse,
    loadUserProfile,
  } = props;

  useEffect(() => {
    const initializeMsal = async () => {
      await msalInstance.initialize();
      const activeAccount = msalInstance.getActiveAccount();
      if (inProgress === InteractionStatus.None && !activeAccount) {
        msalInstance.loginRedirect(loginRequest);
      } else if (activeAccount) {
        const msalClaims = activeAccount.idTokenClaims;
        try {
          const token = await acquireAccessToken();
          api.setSecurityData({ token: token || '', tokenType: 'Bearer' });
          mainAPI.setSecurityData({ token: token || '', tokenType: 'Bearer' });

          if (msalClaims) {
            loadUserProfile(msalClaims);
            initSignalR();
          }
        } catch (error) {
          console.error('Failed to acquire access token:', error);
        }
      }
    };

    initializeMsal();
  }, [inProgress, accounts, msalInstance]);

  useEffect(() => {
    const loadCreditIdentification = async () => {
      await loadCreditIdentificationResponse();
    };

    loadCreditIdentification();
  }, [selectedUserType, loadCreditIdentificationResponse]);

  const initSignalR = async () => {
    if (signalRInitializingRef.current) return;
    if (!props.signalRId && !errored) {
      try {
        signalRInitializingRef.current = true;
        const data = await retrieveSignalRData();
        const { url, accessToken } = data;

        const connection = new signalR.HubConnectionBuilder()
          .withUrl(url, {
            transport: HttpTransportType.WebSockets,
            accessTokenFactory: () => accessToken,
          })
          .configureLogging(signalR.LogLevel.Information)
          .withAutomaticReconnect()
          .build();

        await connection.start();

        props.signalRIdReceived(connection.connectionId);
        bindConnectionMessages(connection);
      } catch (error) {
        setErrored(true);
        setError(error);
        props.signalRSetupFailed(error);
      }
    }
  };

  let disableNavigation = false;
  if (location.pathname.includes('/external/pair') || location.pathname.includes('/blocked')) {
    disableNavigation = true;
  }

  return (
    <ThemeProvider theme={CRPTheme}>
      <CssBaseline />
      <div className={classes.root}>
        {userProfile && (
          <>
            <TermsAndConditions userProfile={userProfile} />
            {!disableNavigation && <Topbar pathname={location.pathname} />}

            {!errored && (
              <div className={classes.content}>
                <BreadcrumbBar />
                {hasBaseRole(BaseRoleCodes.RELEASE_PARTY_CODE) && (
                  <Routes>
                    <Route
                      path="/containers/detail"
                      element={<ReleaseDetail selectedUserType={selectedUserType} />}
                    />
                    <Route
                      path="/releases/detail"
                      element={<ReleaseDetail selectedUserType={selectedUserType} />}
                    />
                    <Route path="/containers/:tabId" element={<ContainerDetailOverview />} />
                    <Route path="/containers" element={<Containers />} />
                    <Route path="/releases" element={<Releases />} />
                    <Route path="/container-subscriptions" element={<ContainerSubscriptions />} />
                    <Route
                      path="/container-subscriptions/detail"
                      element={<SubscriptionDetail selectedUserType={selectedUserType} />}
                    />

                    <Route
                      path="*"
                      element={
                        <BetaRouteSwitch
                          regularRoutes={<Route path="*" element={<RegularReleasePartyRoutes />} />}
                          betaRoutes={<Route path="*" element={<BetaReleasePartyRoutes />} />}
                        />
                      }
                    />
                  </Routes>
                )}
                {hasBaseRole(BaseRoleCodes.TRANSPORT_OPERATOR_CODE) && (
                  <Routes>
                    <Route path="/external/pair/:id" element={<PairedRedirect />} />
                    <Route
                      path="/containers/detail"
                      element={
                        creditIdentificationIsValid(creditIdentificationResponse) &&
                        loadingCreditIdentification === false ? (
                          <ReleaseDetail selectedUserType={selectedUserType} />
                        ) : loadingCreditIdentification === true ? (
                          <CircularProgress className={classes.circularProgress} />
                        ) : (
                          <Navigate to="/blocked" />
                        )
                      }
                    />
                    <Route
                      path="/releases/detail"
                      element={
                        creditIdentificationIsValid(creditIdentificationResponse) &&
                        loadingCreditIdentification === false ? (
                          <ReleaseDetail selectedUserType={selectedUserType} />
                        ) : loadingCreditIdentification === true ? (
                          <CircularProgress className={classes.circularProgress} />
                        ) : (
                          <Navigate to="/blocked" />
                        )
                      }
                    />
                    <Route
                      path="/containers/:tabId"
                      element={
                        creditIdentificationIsValid(creditIdentificationResponse) &&
                        loadingCreditIdentification === false ? (
                          <ContainerDetailOverview />
                        ) : loadingCreditIdentification === true ? (
                          <CircularProgress className={classes.circularProgress} />
                        ) : (
                          <Navigate to="/blocked" />
                        )
                      }
                    />
                    <Route
                      path="/containers"
                      element={
                        creditIdentificationIsValid(creditIdentificationResponse) &&
                        loadingCreditIdentification === false ? (
                          <Containers />
                        ) : loadingCreditIdentification === true ? (
                          <CircularProgress className={classes.circularProgress} />
                        ) : (
                          <Navigate to="/blocked" />
                        )
                      }
                    />
                    <Route
                      path="/releases"
                      element={
                        creditIdentificationIsValid(creditIdentificationResponse) &&
                        loadingCreditIdentification === false ? (
                          <Releases />
                        ) : loadingCreditIdentification === true ? (
                          <CircularProgress className={classes.circularProgress} />
                        ) : (
                          <Navigate to="/blocked" />
                        )
                      }
                    />
                    <Route
                      path="/container-subscriptions"
                      element={
                        creditIdentificationIsValid(creditIdentificationResponse) &&
                        loadingCreditIdentification === false ? (
                          <ContainerSubscriptions />
                        ) : loadingCreditIdentification === true ? (
                          <CircularProgress className={classes.circularProgress} />
                        ) : (
                          <Navigate to="/blocked" />
                        )
                      }
                    />
                    <Route
                      path="/container-subscriptions/detail"
                      element={
                        creditIdentificationIsValid(creditIdentificationResponse) &&
                        loadingCreditIdentification === false ? (
                          <SubscriptionDetail selectedUserType={selectedUserType} />
                        ) : loadingCreditIdentification === true ? (
                          <CircularProgress className={classes.circularProgress} />
                        ) : (
                          <Navigate to="/blocked" />
                        )
                      }
                    />
                    <Route
                      path="/driverlist"
                      element={
                        creditIdentificationIsValid(creditIdentificationResponse) &&
                        loadingCreditIdentification === false ? (
                          <DriverList />
                        ) : loadingCreditIdentification === true ? (
                          <CircularProgress className={classes.circularProgress} />
                        ) : (
                          <Navigate to="/blocked" />
                        )
                      }
                    />
                    <Route path="/blocked" element={<Blocked />} />
                    <Route
                      path="*"
                      element={
                        <BetaRouteSwitch
                          regularRoutes={
                            <Route path="*" element={<RegularTransportOperatorRoutes />} />
                          }
                          betaRoutes={
                            <Route
                              path="*"
                              element={
                                <BetaTransportOperatorRoutes
                                  creditIdentificationIsValid={creditIdentificationIsValid(
                                    creditIdentificationResponse
                                  )}
                                  loadingCreditIdentification={loadingCreditIdentification}
                                />
                              }
                            />
                          }
                        />
                      }
                    />
                  </Routes>
                )}
                {hasBaseRole(BaseRoleCodes.TERMINAL_CODE) && (
                  <Routes>
                    {hasPermission(Permission.CAN_EDIT) && (
                      <Route
                        path="/overrule-customs-light/callback"
                        element={<LoginCallbackForAlfapass />}
                      />
                    )}
                    <Route path="/containers" element={<Containers />} />
                    <Route
                      path="/containers/detail"
                      element={<ReleaseDetail selectedUserType={selectedUserType} />}
                    />
                    <Route
                      path="/releases/detail"
                      element={<ReleaseDetail selectedUserType={selectedUserType} />}
                    />
                    <Route path="/container-subscriptions" element={<ContainerSubscriptions />} />
                    <Route
                      path="/container-subscriptions/detail"
                      element={<SubscriptionDetail selectedUserType={selectedUserType} />}
                    />
                    <Route path="/alfapass" element={<Alfapass />} />
                    <Route
                      path="*"
                      element={
                        <BetaRouteSwitch
                          regularRoutes={<Route path="*" element={<RegularTerminalRoutes />} />}
                          betaRoutes={<Route path="*" element={<BetaTerminalRoutes />} />}
                        />
                      }
                    />
                  </Routes>
                )}
                {hasBaseRole(BaseRoleCodes.SHIP_AGENT_CODE) && (
                  <Routes>
                    <Route path={ROUTE_PATHS.CONTAINERS} element={<Containers />} />
                    {hasPermission(Permission.CAN_EDIT) && (
                      <Route
                        path="/overrule-customs-light/callback"
                        element={<LoginCallbackForAlfapass />}
                      />
                    )}
                    <Route
                      path={ROUTE_PATHS.CONTAINER_DETAIL}
                      element={<ReleaseDetail selectedUserType={selectedUserType} />}
                    />
                    <Route
                      path={ROUTE_PATHS.CONTAINER_DETAIL_OVERVIEW}
                      element={<ContainerDetailOverview />}
                    />
                    <Route
                      path={ROUTE_PATHS.CONTAINER_RELEASE_ADD}
                      element={<ContainerRelease />}
                    />
                    <Route
                      path={ROUTE_PATHS.CONTAINER_RELEASE_UPDATE}
                      element={<ContainerRelease />}
                    />
                    <Route
                      path={ROUTE_PATHS.CONTAINER_SUBSCRIPTIONS}
                      element={<ContainerSubscriptions />}
                    />
                    <Route
                      path={ROUTE_PATHS.CONTAINER_SUBSCRIPTIONS_DETAIL}
                      element={<SubscriptionDetail selectedUserType={selectedUserType} />}
                    />

                    <Route
                      path="*"
                      element={
                        <BetaRouteSwitch
                          regularRoutes={<Route path="*" element={<RegularRoutes />} />}
                          betaRoutes={<Route path="*" element={<BetaRoutes />} />}
                        />
                      }
                    />
                  </Routes>
                )}
              </div>
            )}
            {errored && (
              <Routes>
                <Route path="/" element={<ErrorPage />} />
                <Route path="/containers/:tabId" element={<ErrorPage />} />
                <Route path="/containers" element={<ErrorPage />} />
                <Route path="/releases" element={<ErrorPage />} />
                <Route path="/archive" element={<ErrorPage />} />
                <Route path="/containers/selected" element={<ErrorPage />} />
                <Route path="/container-subscriptions" element={<ErrorPage />} />
              </Routes>
            )}
          </>
        )}
      </div>
      <Messages />
      <ExpiredToken tokenExpired={tokenExpired} tokenExpiring={tokenExpiring} />
    </ThemeProvider>
  );
}

App.propTypes = {
  classes: PropTypes.object,
  loadUserProfile: PropTypes.func.isRequired,
  loadCreditIdentificationResponse: PropTypes.func,
  userProfile: PropTypes.object,
  selectedUserType: PropTypes.string,
  signalRId: PropTypes.string,
};

function mapStateToProps(state) {
  return {
    userProfile: getUserProfile(state),
    selectedUserType: getSelectedUserType(state),
    creditIdentificationResponse: getCreditIdentificationResponse(state),
    signalRId: getSignalRId(state),
    loadingCreditIdentification: getCreditIdentificationLoading(state),
    tokenExpired: getTokenExpired(state),
    tokenExpiring: getTokenExpiring(state),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      signalRIdReceived,
      loadUserProfile,
      loadCreditIdentificationResponse,
      signalRSetupFailed,
    },
    dispatch
  );
}

function creditIdentificationIsValid(creditIdentificationResponse) {
  return (
    creditIdentificationResponse &&
    (creditIdentificationResponse.isTransportCompanyOnly === false ||
      creditIdentificationResponse.creditIdentification != null)
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(App, styles));
